import React from 'react'
import {
  vulnerabilityStatusDescriptionToValue,
  isVulnStatusDescription
} from '../../graphql/schema/shared'
import { defaultStatusRegistry } from './status'
import { FormattedMessage } from 'react-intl'

interface Vulnerability {
  readonly pendingStatus?: null | {
    readonly status: string
  }
  readonly status: string
}

const getStatusInstance = (status: string) =>
  defaultStatusRegistry.fromName(
    isVulnStatusDescription(status)
      ? status
      : vulnerabilityStatusDescriptionToValue[status]
  )

// Render a vulnerability status. Renders pending if defined, then falls back to actual status.
function VulnStatusSingular({
  vulnerability
}: {
  readonly vulnerability: Vulnerability
}) {
  if (vulnerability.pendingStatus) {
    const status = getStatusInstance(vulnerability.pendingStatus.status)
    return (
      <>
        <FormattedMessage id={status.i18nID} />
        <FormattedMessage id="detail_discussion_vulnerabilityStatusPending" />
      </>
    )
  }
  const status = getStatusInstance(vulnerability.status)
  return <FormattedMessage id={status.i18nID} />
}

// Return a list of distinct status names
const getDistinct = (vulnerabilities: Array<undefined | string>) => {
  return Object.keys(
    vulnerabilities.reduce<{ [key: string]: boolean }>((prev, curr) => {
      if (typeof curr === 'undefined') {
        prev.undefined = true
      } else {
        prev[curr] = true
      }
      return prev
    }, {})
  )
}

/**
 * Render a vuln status if all vulns have the same status, otherwise render generic "Status" message.
 * If admin approval workflow is enabled, a vuln may have a pendingStatus. This component takes that into account.
 */
export function VulnStatusDisplay({
  vulnerabilities = []
}: {
  readonly vulnerabilities?: Vulnerability[]
}) {
  if (vulnerabilities.length === 0) {
    // no vulns specified
    return <FormattedMessage id="title_status" />
  }

  if (vulnerabilities.length === 1) {
    // singular
    return <VulnStatusSingular vulnerability={vulnerabilities[0]} />
  }

  const distinctPending = getDistinct(
    vulnerabilities.map((v) => {
      return v.pendingStatus ? v.pendingStatus.status : undefined
    })
  )
  const distinctActual = getDistinct(vulnerabilities.map((v) => v.status))

  if (
    (distinctPending.length === 1 &&
      !distinctPending.find((s) => s === 'undefined')) ||
    distinctActual.length === 1
  ) {
    // multiple, but distinct
    return <VulnStatusSingular vulnerability={vulnerabilities[0]} />
  }

  // mutliple but not distinct
  return <FormattedMessage id="title_status" />
}
